import { Button, Result } from 'antd';
import {
  Component,
  type ErrorInfo,
  type PropsWithChildren,
  useEffect,
  useRef,
} from 'react';
import {
  isRouteErrorResponse,
  useRouteError,
} from 'react-router-dom';

import { reportError } from './errorLogger';

/**
 * Shared fallback UI for both the top-level class boundary and the route-level
 * boundary. Deliberately minimal and design-neutral: a friendly message and a
 * "reload" action so the user is never left staring at a white screen.
 */
function ErrorFallback({ onReload }: { onReload: () => void }) {
  return (
    <Result
      status="error"
      title="Something went wrong"
      subTitle="An unexpected error occurred. Please reload the page — if the problem persists, try again in a few minutes."
      extra={
        <Button type="primary" onClick={onReload}>
          Reload
        </Button>
      }
    />
  );
}

function reloadPage() {
  window.location.reload();
}

type ErrorBoundaryState = { hasError: boolean };

/**
 * Top-level React error boundary. Catches render/lifecycle throws from the
 * subtree, logs the error + component stack in a structured shape, and shows a
 * recoverable fallback instead of white-screening the whole SPA.
 */
export class ErrorBoundary extends Component<
  PropsWithChildren,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    reportError(
      error,
      'react-error-boundary',
      errorInfo.componentStack ?? undefined,
    );
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback onReload={reloadPage} />;
    }
    return this.props.children;
  }
}

/**
 * Route-level error element for `createBrowserRouter`. React Router catches the
 * throw here (rather than letting it bubble to the top-level boundary), so a
 * single route error keeps the rest of the shell alive. Consistent with the
 * data-router approach already used in `AppRouter`.
 */
export function RouteErrorElement() {
  const error = useRouteError();
  const loggedErrorRef = useRef<unknown>(null);

  // Log inside an effect keyed by the error object, not in the render body.
  // React can render this element more than once for a single route error
  // (StrictMode double-render, parent re-render), and logging in render would
  // emit duplicate telemetry each time. Keying the effect on `error` plus a ref
  // guard means we log exactly once per distinct route error even when dev
  // StrictMode replays the mount effect for the same error object.
  useEffect(() => {
    if (loggedErrorRef.current === error) return;
    loggedErrorRef.current = error;
    // Router-generated responses (404/loader throws) aren't app bugs; log them
    // with their status so they're distinguishable from real exceptions.
    if (isRouteErrorResponse(error)) {
      reportError(
        `Route error ${error.status}: ${error.statusText || error.data}`,
        'route-error-boundary',
      );
    } else {
      reportError(error, 'route-error-boundary');
    }
  }, [error]);

  return <ErrorFallback onReload={reloadPage} />;
}
